import { useEffect, useRef, useState } from "react";

import { Paragraph } from "@fronterahealth/frontera-ui-components";
import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { CommentEditor } from "@components/QaReview/components/CommentEditor";
import { EditedLabel } from "@components/QaReview/components/EditedLabel";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";

interface CommentBodyProps {
  comment: ReviewItemFieldsFragment;
  isActive?: boolean;
}

export const CommentBody: React.FC<CommentBodyProps> = ({ comment, isActive = false }) => {
  const { state, dispatch } = useReviewSession();
  const [expandedOverride, setExpandedOverride] = useState<boolean | null>(null);
  const [prevIsActive, setPrevIsActive] = useState(isActive);
  if (isActive !== prevIsActive) {
    setPrevIsActive(isActive);
    setExpandedOverride(null);
  }
  const expanded = expandedOverride ?? isActive;
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const body = comment.content ?? "";

  useEffect(() => {
    const checkTruncation = () => {
      if (!textRef.current) return;
      const { offsetHeight, scrollHeight } = textRef.current;
      if (!expanded) setIsTruncated(scrollHeight > offsetHeight);
    };
    const frameId = requestAnimationFrame(checkTruncation);
    return () => cancelAnimationFrame(frameId);
  }, [body, expanded]);

  if (state.editingCommentId === comment.id) {
    return <CommentEditor comment={comment} onDone={() => dispatch({ type: "stopEditing" })} />;
  }

  return (
    <>
      <div ref={textRef} className={cn(!expanded && "line-clamp-2")}>
        {/* Selectable text; w-fit leaves the space beside it to the go-to button. */}
        <Paragraph
          size="extra-small"
          colorType="primary"
          className="pointer-events-auto w-fit text-gray-600 whitespace-pre-wrap"
          data-dd-action-name="comment text"
        >
          {body}
        </Paragraph>
      </div>

      {!expanded && isTruncated && (
        <Button
          variant="link"
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            setExpandedOverride(true);
          }}
        >
          Show more
        </Button>
      )}

      {expanded && isTruncated && (
        <Button
          variant="link"
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            setExpandedOverride(false);
          }}
        >
          Show less
        </Button>
      )}

      {comment.__typename === "ReportCommentType" && comment.editedAt && <EditedLabel editedAt={comment.editedAt} />}
    </>
  );
};
